/**
 * Whisper AI 가사 추출 서비스
 */

const API_BASE_URL = import.meta.env.DEV ? 'http://localhost:5000' : '';

/**
 * 가사 추출 요청 (비동기)
 * @param {string} videoId - YouTube 비디오 ID
 * @param {number} startTime - 시작 시간 (초)
 * @param {number} endTime - 종료 시간 (초)
 * @param {string} outputLanguage - 출력 언어 (ko/en/ja)
 * @returns {Promise<{jobId: string, status: string}>}
 */
export async function extractLyrics(videoId, startTime, endTime, outputLanguage = 'ko') {
  const token = localStorage.getItem('ttaechang_token');

  if (!token) {
    throw new Error('로그인이 필요합니다.');
  }

  const response = await fetch(`${API_BASE_URL}/api/lyrics/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      videoId,
      startTime,
      endTime,
      outputLanguage
    })
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
    }

    let errorMessage = '가사 추출 요청 실패';
    try {
      const error = await response.json();
      errorMessage = error.error || errorMessage;
    } catch (e) {
      // JSON 파싱 실패 시 기본 메시지 사용
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

/**
 * 가사 추출 상태 확인
 * @param {string} jobId - 작업 ID
 * @returns {Promise<{status: string, lyrics: Array}>}
 */
export async function getLyricsStatus(jobId) {
  const token = localStorage.getItem('ttaechang_token');

  if (!token) {
    throw new Error('로그인이 필요합니다.');
  }

  const response = await fetch(`${API_BASE_URL}/api/lyrics/status/${jobId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
    }

    let errorMessage = '상태 확인 실패';
    try {
      const error = await response.json();
      errorMessage = error.error || errorMessage;
    } catch (e) {
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

/**
 * 가사 추출 폴링 (완료될 때까지 자동 확인)
 * @param {string} jobId - 작업 ID
 * @param {Function} onProgress - 진행 상황 콜백 (status)
 * @param {number} maxAttempts - 최대 시도 횟수 (기본: 150회 = 5분)
 * @returns {Promise<Array>} - 추출된 가사 배열
 */
export async function pollLyricsStatus(jobId, onProgress = null, maxAttempts = 150) {
  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;

    try {
      const result = await getLyricsStatus(jobId);

      // 진행 상황 콜백 호출
      if (onProgress) {
        onProgress(result.status, attempts, maxAttempts);
      }

      // 완료
      if (result.status === 'completed') {
        return result.lyrics;
      }

      // 실패
      if (result.status === 'failed') {
        throw new Error(result.error || '가사 추출 실패');
      }

      // pending 또는 processing - 2초 대기
      await new Promise(resolve => setTimeout(resolve, 2000));

    } catch (error) {
      console.error('폴링 오류:', error);
      throw error;
    }
  }

  // 타임아웃
  throw new Error('가사 추출 시간이 초과되었습니다.');
}

/**
 * Whisper 서버 상태 확인
 * @returns {Promise<{status: string, whisper: Object}>}
 */
export async function getWhisperHealth() {
  const response = await fetch(`${API_BASE_URL}/api/lyrics/health`, {
    method: 'GET'
  });

  if (!response.ok) {
    throw new Error('Whisper 서버 상태 확인 실패');
  }

  return await response.json();
}
